/**
 * QuickSaleKitchenOrdersModal.tsx
 *
 * Hızlı satış / restoran modundan mutfak ekranına gönderilen siparişleri gösteren modal.
 * - SADECE bugünün siparişlerini listeler (tarih sınırı: bugünün başlangıcı → yarın 00:00)
 * - Mod bazlı filtreleme: Restoran modu → `-RS-`, Hızlı Satış modu → `-HS-` siparişleri
 * - Şube bazlı filtreleme: API'ye `branchId` geçilir, ayrıca client-side QR menü eşleşmesi güvenlik ağı olarak durur
 * - Durum güncelleme, fiş yeniden yazdırma
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { mainApi } from '../../../../../config/apiClient';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Restaurant as RestaurantIcon,
  AccessTime as AccessTimeIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { usePrintReceipt } from '../PrintReceiptService';
import GuideButton from '../../../../common/guide/GuideButton';
import type { CartItem } from '../../QuickSaleScreen';

// Sipariş tipi
interface KitchenOrder {
  id: number;
  siparisId: string;
  shortCode: string;
  masaNumarasi: number | null;
  masaAdi: string | null;
  kullaniciAdi: string;
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  toplamTutar: number;
  siparisDetaylari: Array<{
    urunAdi: string;
    miktar: number;
    birimFiyat: number;
  }>;
  siparisNotu?: string;
  qrMenuId?: number;
  odemeYontemi?: string | null;
  cariHesapAdi?: string | null;
  restoranMasaAdi?: string | null;
}

// Durum buton bilgisi
interface StatusButtonInfo {
  label: string;
  emoji: string;
  color: 'primary' | 'success' | 'info' | 'warning' | 'secondary';
  nextStatus: string | null;
  disabled: boolean;
}

interface QuickSaleKitchenOrdersModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  branchId: string | null;
  selectedQRMenuId: string | null;
  screenId: string | null;
  screenSettings?: any;
  isRestaurantMode?: boolean;
  /** ✅ PR2 (Invoice Seller Snapshot Refactor): Franchise fiş başlığı için satıcı snapshot. */
  seller?: import('../../../../../services/sellerInfoService').SellerSnapshot | null;
}

const QuickSaleKitchenOrdersModal: React.FC<QuickSaleKitchenOrdersModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
  selectedQRMenuId,
  screenId,
  screenSettings,
  isRestaurantMode = false,
  seller = null,
}) => {
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [updating, setUpdating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printSnackbar, setPrintSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  
  // Şubeye ait QR menü ID'leri
  const allowedQrMenuIdsRef = useRef<Set<string>>(new Set());
  const [allowedQrMenuIdsLoaded, setAllowedQrMenuIdsLoaded] = useState(false);

  // Fiş yazdırma hook'u
  const { printReceipt } = usePrintReceipt({
    companyId,
    screenId,
    screenSettings,
    seller, // ✅ PR2: franchise fiş başlığı için
    onPrintError: (err) => {
      console.error('[KitchenOrdersModal] Fiş yazdırma hatası:', err);
      setPrintSnackbar({ open: true, message: `Fiş yazdırma hatası: ${err}`, severity: 'error' });
      setPrinting(false);
    },
    onPrintSuccess: (msg) => {
      console.log('[KitchenOrdersModal] Fiş yazdırıldı:', msg);
      setPrintSnackbar({ open: true, message: 'Fiş başarıyla yazdırıldı!', severity: 'success' });
      setPrinting(false);
    },
    lazyLoad: true,
  });

  // Seçili sipariş
  const selectedOrder = orders.find(o => o.id === selectedOrderId) || null;

  // Sipariş ID'sinden kısa kodu çıkar
  const getShortOrderCode = (siparisId: string): string => {
    // HS siparişleri: "HS-20260109-141039-817-HS-19" → "HS-19"
    if (siparisId.includes('-HS-')) {
      const parts = siparisId.split('-HS-');
      if (parts.length === 2) return `HS-${parts[1]}`;
    }
    // RS siparişleri: "RS-20260109-141039-817-RS-3" → "RS-3"
    if (siparisId.includes('-RS-')) {
      const parts = siparisId.split('-RS-');
      if (parts.length === 2) return `RS-${parts[1]}`;
    }
    return siparisId.length > 10 ? siparisId.substring(siparisId.length - 6) : siparisId;
  };

  // Durum rengini belirle
  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'info' | 'error' => {
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus === 'beklemede' || normalizedStatus === 'yeni') return 'warning';
    if (normalizedStatus === 'hazırlanıyor') return 'primary';
    if (normalizedStatus === 'hazır') return 'info';
    if (normalizedStatus === 'teslim edildi') return 'success';
    if (normalizedStatus === 'iptal') return 'error';
    return 'default';
  };

  // Bekleme süresini hesapla
  const getWaitingMinutes = (createdAt: string): number => {
    const created = new Date(createdAt);
    return Math.floor((Date.now() - created.getTime()) / 60000);
  };

  // Bekleme süresini formatla
  const formatWaitingTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes} dk`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}s ${mins}dk` : `${hours} saat`;
  };

  // Durum buton bilgisini hesapla (PackageOrderScreen gibi)
  const getStatusButtonInfo = (): StatusButtonInfo => {
    if (!selectedOrder) {
      return {
        label: 'Sipariş Seç',
        emoji: '👆',
        color: 'secondary',
        nextStatus: null,
        disabled: true,
      };
    }

    const status = selectedOrder.siparisDurumu.toLowerCase();

    if (status === 'beklemede' || status === 'yeni' || status === 'hazırlanıyor') {
      return {
        label: 'HAZIR',
        emoji: '✅',
        color: 'success',
        nextStatus: 'Hazır',
        disabled: false,
      };
    }

    if (status === 'hazır') {
      return {
        label: 'TESLİM EDİLDİ',
        emoji: '🍽️',
        color: 'info',
        nextStatus: 'Teslim Edildi',
        disabled: false,
      };
    }

    if (status === 'teslim edildi') {
      return {
        label: 'TESLİM EDİLDİ',
        emoji: '✅',
        color: 'success',
        nextStatus: null,
        disabled: true,
      };
    }

    return {
      label: 'İŞLEM YOK',
      emoji: '⏸️',
      color: 'secondary',
      nextStatus: null,
      disabled: true,
    };
  };

  // Şubeye ait QR menü ID'lerini yükle
  const loadAllowedQrMenuIds = useCallback(async () => {
    if (!branchId || !companyId) {
      allowedQrMenuIdsRef.current = new Set();
      setAllowedQrMenuIdsLoaded(true);
      return;
    }

    try {
      const response = await mainApi.get(`/api/QRMenu/branch/${branchId}/menu-ids`);
      const result = response.data;
      const isSuccess = result.Success || result.success;
      const data = result.Data || result.data;
      
      if (isSuccess && data && data.length > 0) {
        allowedQrMenuIdsRef.current = new Set<string>(data);
        console.log(`✅ [KitchenOrdersModal] Şube ${branchId} için ${data.length} QR menü ID yüklendi`);
      } else {
        allowedQrMenuIdsRef.current = new Set(['__NONE__']);
        console.warn(`⚠️ [KitchenOrdersModal] Şube ${branchId} için QR menü bulunamadı`);
      }
    } catch (error) {
      console.error('❌ [KitchenOrdersModal] QR menü ID yükleme hatası:', error);
      allowedQrMenuIdsRef.current = new Set();
    }
    
    setAllowedQrMenuIdsLoaded(true);
  }, [companyId, branchId]);

  // Siparişin bu şubeye ait olup olmadığını kontrol et
  const isOrderForThisBranch = useCallback((order: KitchenOrder): boolean => {
    // Şube tanımlı değilse tüm siparişleri göster
    if (!branchId) return true;
    
    // Henüz yüklenmediyse geçici olarak göster
    if (!allowedQrMenuIdsLoaded) return true;
    
    // __NONE__ varsa hiç gösterme
    if (allowedQrMenuIdsRef.current.has('__NONE__')) return false;
    
    // Eğer seçili QR menü ID varsa, sadece o menünün siparişlerini göster
    if (selectedQRMenuId && order.qrMenuId) {
      return String(order.qrMenuId) === selectedQRMenuId;
    }
    
    // qrMenuId yoksa (eski siparişler), geçir
    if (!order.qrMenuId) return true;
    
    // Şubeye ait menülerden mi kontrol et
    return allowedQrMenuIdsRef.current.has(String(order.qrMenuId));
  }, [branchId, allowedQrMenuIdsLoaded, selectedQRMenuId]);

  // Siparişleri yükle
  const loadOrders = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      // ✅ Sadece bugünün siparişleri (tarih filtresini API'ye gönder, aksi halde tüm geçmiş siparişler çekiliyor)
      // Backend: SiparisOlusturmaTarihi >= startDate.Date && < endDate.Date.AddDays(1)
      // Yani startDate=bugün & endDate=bugün → bugün 00:00 ile yarın 00:00 arası.
      const now = new Date();
      const todayIsoDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const params = new URLSearchParams();
      params.set('startDate', todayIsoDate);
      params.set('endDate', todayIsoDate);
      if (branchId) params.set('branchId', branchId);

      const response = await mainApi.get(`/api/MasaSiparisi/orders?${params.toString()}`);
      const result = response.data;

        if (result.success && result.data) {
          // Filtreleme:
          // 1. Mod bazlı sipariş filtresi: Restoran → '-RS-', Hızlı Satış → '-HS-'
          // 2. Tarih filtresi API'ye gönderildiği için client'ta yalnızca status/şube kontrolü
          // 3. Şube kontrolü (API-side filtreleme + güvenlik ağı olarak QR menü eşleşmesi)
          // 4. Aktif siparişler (İptal ve Teslim Edildi hariç)
          const orderPrefix = isRestaurantMode ? '-RS-' : '-HS-';
          const filteredOrders: KitchenOrder[] = result.data
            .filter((order: any) => {
              if (!order.siparisId?.includes(orderPrefix)) return false;
              
              // İptal edilmiş mi?
              if (order.siparisDurumu?.toLowerCase() === 'iptal') return false;
              
              // Teslim edilmiş mi? (teslim edilen siparişleri gösterme)
              if (order.siparisDurumu?.toLowerCase() === 'teslim edildi') return false;
              
              return true;
            })
            .map((order: any) => ({
              id: order.id,
              siparisId: order.siparisId,
              shortCode: getShortOrderCode(order.siparisId),
              masaNumarasi: order.masaNumarasi,
              masaAdi: order.masaAdi,
              kullaniciAdi: order.kullaniciAdi || 'Anonim',
              siparisDurumu: order.siparisDurumu || 'Beklemede',
              siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
              toplamTutar: order.toplamTutar || 0,
              siparisDetaylari: (() => {
                let detaylar = order.siparisDetaylari;
                if (typeof detaylar === 'string') {
                  try {
                    const parsed = JSON.parse(detaylar);
                    detaylar = parsed.siparisDetaylari || parsed;
                  } catch { detaylar = []; }
                }
                if (detaylar && !Array.isArray(detaylar) && (detaylar as any).siparisDetaylari) {
                  detaylar = (detaylar as any).siparisDetaylari;
                }
                return Array.isArray(detaylar) ? detaylar : [];
              })(),
              siparisNotu: (() => {
                if (order.siparisNotu) return order.siparisNotu;
                if (typeof order.siparisDetaylari === 'string') {
                  try {
                    const parsed = JSON.parse(order.siparisDetaylari);
                    return parsed.siparisNotu || undefined;
                  } catch { return undefined; }
                }
                return undefined;
              })(),
              qrMenuId: order.qrMenuId,
              odemeYontemi: order.odemeYontemi || null,
              cariHesapAdi: (() => {
                if (order.cariHesapAdi) return order.cariHesapAdi;
                if (typeof order.siparisDetaylari === 'string') {
                  try {
                    const parsed = JSON.parse(order.siparisDetaylari);
                    return parsed.cariHesapAdi || null;
                  } catch { return null; }
                }
                return null;
              })(),
              restoranMasaAdi: (() => {
                if (order.masaAdi) return order.masaAdi;
                if (typeof order.siparisDetaylari === 'string') {
                  try {
                    const parsed = JSON.parse(order.siparisDetaylari);
                    return parsed.masaAdi || null;
                  } catch { return null; }
                }
                return null;
              })(),
            }))
            .filter((order: KitchenOrder) => isOrderForThisBranch(order));
          
          // En yeni siparişler üstte
          filteredOrders.sort((a, b) => 
            new Date(b.siparisOlusturmaTarihi).getTime() - new Date(a.siparisOlusturmaTarihi).getTime()
          );
          
          setOrders(filteredOrders);
          console.log(`✅ [KitchenOrdersModal] ${filteredOrders.length} sipariş yüklendi`);
        }
    } catch (err: any) {
      console.error('❌ [KitchenOrdersModal] Sipariş yükleme hatası:', err);
      setError('Siparişler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, isRestaurantMode, isOrderForThisBranch]);

  // Durum güncelle
  const handleUpdateStatus = useCallback(async () => {
    const statusInfo = getStatusButtonInfo();
    
    if (!selectedOrder || !statusInfo.nextStatus || !companyId) return;
    
    setUpdating(true);
    
    try {
      await mainApi.post(
        `/api/MasaSiparisi/order/${selectedOrder.id}/status`,
        { siparisDurumu: statusInfo.nextStatus }
      );
      
      console.log(`✅ [KitchenOrdersModal] Sipariş ${selectedOrder.id} durumu güncellendi: ${statusInfo.nextStatus}`);
        
        // Listeyi güncelle
        setOrders(prev => prev.map(order => 
          order.id === selectedOrder.id
            ? { ...order, siparisDurumu: statusInfo.nextStatus! }
            : order
        ));
        
        // Teslim edildi ise seçimi kaldır
        if (statusInfo.nextStatus === 'Teslim Edildi') {
          setSelectedOrderId(null);
        }
    } catch (err: any) {
      console.error('❌ [KitchenOrdersModal] Durum güncelleme hatası:', err);
      setError('Sipariş durumu güncellenirken bir hata oluştu.');
    } finally {
      setUpdating(false);
    }
  }, [selectedOrder, companyId]);

  // Seçili siparişin fişini yeniden yazdır
  const handlePrintReceipt = useCallback(async () => {
    if (!selectedOrder) return;

    // 🚦 "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" ayarı kapalı ise
    // bu sistemde HİÇBİR şekilde fiş yazdırılmaz — mutfak siparişi
    // tekrar basımı da dahil. Kullanıcının açık direktifi.
    // @ts-ignore - useBothFiscalAndReceiptPrinter screenSettings'de var
    const useBothFiscalAndReceiptPrinter = screenSettings?.useBothFiscalAndReceiptPrinter || false;
    if (!useBothFiscalAndReceiptPrinter) {
      console.log(
        '[KitchenOrdersModal] 🚫 "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" KAPALI — tekrar fiş basımı engellendi ' +
          `(orderId=${selectedOrder.id}, shortCode=${selectedOrder.shortCode})`,
      );
      setPrintSnackbar({
        open: true,
        message: '"Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" ayarı kapalı. Fiş basımı için ayarı açın.',
        severity: 'error',
      });
      return;
    }

    setPrinting(true);
    
    try {
      const orderDate = new Date(selectedOrder.siparisOlusturmaTarihi);
      const formattedDate = orderDate.toLocaleDateString('tr-TR');
      const formattedTime = orderDate.toLocaleTimeString('tr-TR');
      
      // siparisDetaylari JSON string olabilir, güvenli şekilde parse et
      let detaylar = selectedOrder.siparisDetaylari;
      if (typeof detaylar === 'string') {
        try {
          const parsed = JSON.parse(detaylar);
          detaylar = parsed.siparisDetaylari || parsed;
        } catch { detaylar = []; }
      }
      if (detaylar && !Array.isArray(detaylar) && (detaylar as any).siparisDetaylari) {
        detaylar = (detaylar as any).siparisDetaylari;
      }
      if (!Array.isArray(detaylar)) detaylar = [];
      
      const cartItems: CartItem[] = detaylar.map((item: any, index: number) => ({
        id: `kitchen-${selectedOrder.id}-${index}`,
        cartItemId: `kitchen-${selectedOrder.id}-${index}`,
        name: item.urunAdi,
        price: item.birimFiyat,
        quantity: item.miktar,
        vatRate: item.vatRate || 0,
        vatStatus: 'included' as const,
        unit: item.birim || 'Adet',
      }));
      
      const customerNameForReceipt = isRestaurantMode
        ? (selectedOrder.cariHesapAdi || selectedOrder.kullaniciAdi || 'Genel Cari')
        : (selectedOrder.kullaniciAdi || 'Genel Cari');

      const tableNameForReceipt = isRestaurantMode
        ? (selectedOrder.restoranMasaAdi || selectedOrder.masaAdi || (selectedOrder.masaNumarasi ? `Masa ${selectedOrder.masaNumarasi}` : undefined))
        : undefined;

      const paymentMethodMap: Record<string, string> = {
        'Nakit': 'NAKİT',
        'Kredi Kartı': 'KREDİ KARTI',
        'Karma': 'KARMA',
      };
      const paymentMethodForReceipt = selectedOrder.odemeYontemi
        ? (paymentMethodMap[selectedOrder.odemeYontemi] || selectedOrder.odemeYontemi)
        : 'TEKRAR BASIM';

      const receiptData = {
        invoiceNumber: selectedOrder.siparisId,
        date: formattedDate,
        time: formattedTime,
        customerName: customerNameForReceipt,
        tableName: tableNameForReceipt,
        subtotal: selectedOrder.toplamTutar,
        discountTotal: 0,
        grandTotal: selectedOrder.toplamTutar,
        paymentMethod: paymentMethodForReceipt,
        showCashierName: false,
        showReceiptNumber: false,
        orderNumber: selectedOrder.shortCode,
      };
      
      console.log('[KitchenOrdersModal] Fiş yazdırılıyor:', { orderId: selectedOrder.id, shortCode: selectedOrder.shortCode, itemCount: cartItems.length, detaylarType: typeof selectedOrder.siparisDetaylari });
      
      await printReceipt(receiptData as any, cartItems);
    } catch (err: any) {
      console.error('[KitchenOrdersModal] Fiş yazdırma hatası:', err);
      setPrintSnackbar({ open: true, message: `Fiş yazdırma hatası: ${err.message || 'Bilinmeyen hata'}`, severity: 'error' });
      setPrinting(false);
    }
  }, [selectedOrder, printReceipt]);

  // Otomatik yenileme (30 saniyede bir)
  useEffect(() => {
    if (!open || !companyId) return;
    
    const interval = setInterval(() => {
      console.log('🔄 [KitchenOrdersModal] Otomatik yenileme...');
      loadOrders();
    }, 30000); // 30 saniye
    
    return () => clearInterval(interval);
  }, [open, companyId, loadOrders]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open) {
      loadAllowedQrMenuIds().then(() => {
        loadOrders();
      });
      setSelectedOrderId(null);
    }
  }, [open, loadAllowedQrMenuIds, loadOrders]);

  const statusButtonInfo = getStatusButtonInfo();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestaurantIcon />
          <Typography variant="h6">Mutfak Siparişleri</Typography>
          <Chip 
            label={`${orders.length} sipariş`} 
            size="small" 
            sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Yenile">
            <IconButton onClick={loadOrders} disabled={loading} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <GuideButton guideKey="sales.kitchenOrders" sx={{ color: 'white' }} tabIndex={-1} />
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <RestaurantIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography color="text.secondary">
              Bugün için teslim edilmemiş mutfak siparişi bulunmuyor.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Sipariş No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Tarih / Saat</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Bekleme</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Tutar</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const isSelected = selectedOrderId === order.id;
                  const waitingMinutes = getWaitingMinutes(order.siparisOlusturmaTarihi);
                  
                  return (
                    <TableRow
                      key={order.id}
                      onClick={() => setSelectedOrderId(order.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.light' : 'inherit',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.light' : 'action.hover',
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight={isSelected ? 'bold' : 'normal'}
                          color={isSelected ? 'primary.contrastText' : 'inherit'}
                        >
                          {order.shortCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const orderDate = new Date(order.siparisOlusturmaTarihi);
                          const todayDate = new Date();
                          todayDate.setHours(0, 0, 0, 0);
                          const isToday = orderDate >= todayDate;
                          const isOld = !isToday;
                          
                          return (
                            <Box>
                              {isOld && (
                                <Typography 
                                  variant="caption" 
                                  color={isSelected ? 'primary.contrastText' : 'error.main'}
                                  fontWeight="bold"
                                  display="block"
                                >
                                  {orderDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                                </Typography>
                              )}
                              <Typography 
                                variant="body2"
                                color={isSelected ? 'primary.contrastText' : isOld ? 'error.main' : 'inherit'}
                                fontWeight={isOld ? 'bold' : 'normal'}
                              >
                                {orderDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTimeIcon 
                            sx={{ 
                              fontSize: 16, 
                              color: waitingMinutes > 30 
                                ? 'error.main' 
                                : waitingMinutes > 15 
                                  ? 'warning.main' 
                                  : isSelected 
                                    ? 'primary.contrastText' 
                                    : 'text.secondary' 
                            }} 
                          />
                          <Typography 
                            variant="body2"
                            color={
                              waitingMinutes > 30 
                                ? 'error.main' 
                                : waitingMinutes > 15 
                                  ? 'warning.main' 
                                  : isSelected 
                                    ? 'primary.contrastText' 
                                    : 'text.secondary'
                            }
                            fontWeight={waitingMinutes > 15 ? 'bold' : 'normal'}
                          >
                            {formatWaitingTime(waitingMinutes)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          color={isSelected ? 'primary.contrastText' : 'inherit'}
                        >
                          ₺{order.toplamTutar.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.siparisDurumu}
                          size="small"
                          color={getStatusColor(order.siparisDurumu)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        justifyContent: 'space-between',
      }}>
        <Box>
          {selectedOrder && (
            <Typography variant="body2" color="text.secondary">
              Seçili: <strong>{selectedOrder.shortCode}</strong> - {selectedOrder.siparisDurumu}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            Kapat
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={handlePrintReceipt}
            disabled={!selectedOrder || printing}
            startIcon={printing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
            sx={{ minWidth: 150 }}
          >
            {printing ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
          </Button>
          <Button
            variant="contained"
            color={statusButtonInfo.color}
            onClick={handleUpdateStatus}
            disabled={statusButtonInfo.disabled || updating}
            startIcon={updating ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ minWidth: 180 }}
          >
            {statusButtonInfo.emoji} {statusButtonInfo.label}
          </Button>
        </Box>
      </DialogActions>

      <Snackbar
        open={printSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setPrintSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setPrintSnackbar(prev => ({ ...prev, open: false }))}
          severity={printSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {printSnackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default QuickSaleKitchenOrdersModal;
